import React, { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useAtom } from 'jotai';
import { user_info } from 'jotai/UserJotaiInstance';
import PageBreadcrumb from '../../../components/common/PageBreadcrumb';
import AgentMLSDashboard from './agent/AgentMLSDashboard';
import ClientMLSDashboard from './client/ClientMLSDashboard';
import PortalNavigation from './components/PortalNavigation';
import { MLSChatbot } from '../../../components/mls-chatbot';
import './MLSPortal.css';

interface UserProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role?: string;
  userType?: string;
  subscription_package?: {
    subscription_package_name: string;
  };
}

const MLSPortal: React.FC = () => {
  const [userInfo] = useAtom(user_info);
  const [activeTab, setActiveTab] = useState('search');
  const [showChatbot, setShowChatbot] = useState(false);
  const [isAgent, setIsAgent] = useState(false);

  useEffect(() => {
    // Determine if user is an agent based on role or subscription
    if (userInfo) {
      const userProfile = userInfo as UserProfile;
      const isAgentRole = userProfile.role === 'agent' || 
                         userProfile.userType === 'agent' ||
                         ['Premium', 'Professional', 'BrokerTeamPlan', 'CorporatePlan', 'EnterprisePlan'].includes(
                           userProfile.subscription_package?.subscription_package_name || ''
                         );
      setIsAgent(isAgentRole);
      
      // Set appropriate default tab
      setActiveTab(isAgentRole ? 'dashboard' : 'search');
    }
  }, [userInfo]);

  const handleChatbotPropertySearch = (criteria: any) => {
    // Handle property search triggered from chatbot
    console.log('Property search from chatbot:', criteria);
    setActiveTab('search');
    // TODO: Pass search criteria to search component
  };

  const handleChatbotScheduleViewing = (propertyId: string) => {
    // Handle viewing schedule triggered from chatbot
    console.log('Schedule viewing from chatbot:', propertyId);
    // TODO: Open scheduling modal or component
  };

  const agentTabs = [
    { id: 'dashboard', label: 'Dashboard', icon: 'grid' },
    { id: 'listings', label: 'My Listings', icon: 'home' },
    { id: 'clients', label: 'Client Matcher', icon: 'users' },
    { id: 'analytics', label: 'Analytics', icon: 'bar-chart-2' },
    { id: 'search', label: 'Property Search', icon: 'search' }
  ];

  const clientTabs = [
    { id: 'search', label: 'Search Properties', icon: 'search' },
    { id: 'recommendations', label: 'Recommendations', icon: 'star' },
    { id: 'saved', label: 'Saved Properties', icon: 'heart' },
    { id: 'tours', label: 'Virtual Tours', icon: 'eye' }
  ];

  const tabs = isAgent ? agentTabs : clientTabs;

  return (
    <div className="mls-portal">
      <PageBreadcrumb
        items={[
          { label: 'Home', url: '/' },
          { label: 'MLS Portal', active: true }
        ]}
      />
      
      <Container fluid>
        <Row>
          <Col lg={showChatbot ? 9 : 12}>
            {/* Portal Header */}
            <div className="portal-header mb-4">
              <Row className="align-items-center">
                <Col>
                  <h1 className="h3 mb-0">
                    {isAgent ? '🏠 MLS Portal - Agent View' : '🏠 Find Your Dream Home'}
                  </h1>
                  <p className="text-muted mb-0">
                    {isAgent 
                      ? 'Manage properties and match clients with AI-powered tools'
                      : 'Discover properties with personalized AI recommendations'
                    }
                  </p>
                </Col>
                <Col xs="auto">
                  <div className="d-flex gap-2">
                    <button 
                      className={`btn btn-${showChatbot ? 'primary' : 'outline-primary'} btn-sm`}
                      onClick={() => setShowChatbot(!showChatbot)}
                    >
                      💬 AI Assistant
                    </button>
                    {userInfo && (
                      <div className="user-info text-end">
                        <small className="text-muted d-block">Welcome back,</small>
                        <strong>{(userInfo as UserProfile).first_name}</strong>
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
            </div>

            {/* Navigation Tabs */}
            <PortalNavigation 
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />

            {/* Main Content Area */}
            <div className="portal-content mt-4">
              {isAgent ? (
                <AgentMLSDashboard 
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              ) : (
                <ClientMLSDashboard 
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              )}
            </div>
          </Col>

          {/* Chatbot Sidebar */}
          {showChatbot && (
            <Col lg={3} className="chatbot-sidebar">
              <div className="sticky-top" style={{ top: '20px' }}>
                <MLSChatbot />
              </div>
            </Col>
          )}
        </Row>
      </Container>
    </div>
  );
};

export default MLSPortal;